import type { AuthContextValue } from "@/contexts/AuthContext";
import type { OrgLocation } from "@/config/locations";
import type { Location } from "@/types/location";
import { SettingsDrawerPasswordResetInline } from "./SettingsDrawerPasswordResetInline";
import { formatPhoneVisual, phoneDigits } from "./settingsDrawerPhone";
import type { SettingsDrawerAccountModel } from "./useSettingsDrawerAccount";

export function SettingsDrawerProfileSection(props: {
  auth: AuthContextValue;
  account: SettingsDrawerAccountModel;
  showLeadsControls: boolean;
  location: Location | null;
  selectableLocations: OrgLocation[];
  setLocation: (l: Location) => void;
  locationsLoaded: boolean;
}) {
  const {
    auth,
    account,
    showLeadsControls,
    location,
    selectableLocations,
    setLocation,
    locationsLoaded,
  } = props;

  const {
    accountOpen,
    setAccountOpen,
    fullName,
    setFullName,
    email,
    setEmail,
    personalPhone,
    setPersonalPhone,
    workPhone,
    setWorkPhone,
    accountSaving,
    accountMsg,
    suggestedOfficePhone,
    normalizedEmail,
    effectiveWorkPhone,
    accountEssentialReady,
    incompleteAccountFields,
    hasAccountChanges,
    saveAccountProfile,
  } = account;

  return (
    <section className="space-y-3">
      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50/70 dark:bg-gray-800/60 overflow-hidden">
        <button
          type="button"
          onClick={() => setAccountOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-3 px-3 py-3 text-left"
        >
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">
                Profile
              </span>
              <span
                className={
                  "text-[11px] rounded-full px-2 py-0.5 border " +
                  (accountEssentialReady
                    ? "border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-200"
                    : "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200")
                }
              >
                {accountEssentialReady ? "Ready" : "Needs info"}
              </span>
            </div>
            <div className="mt-1 text-xs text-gray-600 dark:text-gray-300 truncate">
              {accountEssentialReady
                ? `${fullName.trim() || "No name"} • ${normalizedEmail || "No email"} • ${formatPhoneVisual(effectiveWorkPhone)}`
                : `Complete: ${incompleteAccountFields.join(", ")}`}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {hasAccountChanges ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  void saveAccountProfile();
                }}
                disabled={accountSaving}
                className={
                  "text-xs px-3 py-1.5 rounded-full border " +
                  (accountSaving
                    ? "border-gray-200 dark:border-gray-700 text-gray-400 cursor-not-allowed"
                    : "border-indigo-300 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-950 text-indigo-900 dark:text-indigo-200 hover:bg-indigo-100 dark:hover:bg-indigo-900")
                }
              >
                {accountSaving ? "Saving…" : "Save"}
              </button>
            ) : null}
            <span className="text-xs text-gray-400">{accountOpen ? "▾" : "▸"}</span>
          </div>
        </button>

        {accountOpen ? (
          <div className="border-t border-gray-200 dark:border-gray-700 px-3 py-3 space-y-3">
            {!accountEssentialReady ? (
              <div className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950/50 dark:text-amber-200">
                Complete this profile: {incompleteAccountFields.join(", ")}.
              </div>
            ) : null}

            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Full name</label>
              <input
                className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Your full name"
              />
            </div>

            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Email</label>
              <input
                className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                type="email"
              />
            </div>

            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Work number</label>
              <input
                className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                value={formatPhoneVisual(workPhone)}
                onChange={(e) => setWorkPhone(phoneDigits(e.target.value))}
                placeholder={formatPhoneVisual(suggestedOfficePhone) || "Office or shared number"}
                inputMode="tel"
              />
              {auth.role === "sales" && suggestedOfficePhone ? (
                <div className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                  Sales default: {formatPhoneVisual(suggestedOfficePhone)} from your active office.
                </div>
              ) : (
                <div className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">Fill intentionally for non-sales roles.</div>
              )}
            </div>

            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Personal number (optional)</label>
              <input
                className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                value={formatPhoneVisual(personalPhone)}
                onChange={(e) => setPersonalPhone(phoneDigits(e.target.value))}
                placeholder="Your direct number"
                inputMode="tel"
              />
            </div>

            <div className="text-[11px] text-gray-500 dark:text-gray-400">
              KB edits now use this signed-in identity automatically.
            </div>

            <SettingsDrawerPasswordResetInline
              email={(normalizedEmail || auth.session?.user?.email || "").trim()}
            />

            {showLeadsControls ? (
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Working location</label>
                <select
                  disabled={!locationsLoaded || selectableLocations.length === 0}
                  className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                  value={location?.id || ""}
                  onChange={(e) => {
                    const id = e.target.value;
                    const loc = selectableLocations.find((x) => x.id === id);
                    if (!loc) return;
                    setLocation({
                      id: loc.id,
                      name: loc.fullName,
                      address: loc.address ?? "",
                      phone: loc.phone ?? "",
                    });
                  }}
                >
                  <option value="">
                    {!locationsLoaded
                      ? "Loading locations..."
                      : selectableLocations.length === 0
                        ? "No assigned locations"
                        : "Select location..."}
                  </option>
                  {selectableLocations.map((loc) => (
                    <option key={loc.id} value={loc.id}>
                      {loc.fullName}
                    </option>
                  ))}
                </select>
                <div className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                  Sales users can select from assigned locations only.
                </div>
              </div>
            ) : null}

            {accountMsg ? (
              <div className="text-xs rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2">
                {accountMsg}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}
